import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useSession } from '../lib/session.js';

/* ============================================================================
   The client-side gate.
   ----------------------------------------------------------------------------
   This is a courtesy, NOT a security boundary. Every protected endpoint checks
   the session itself; this only stops the app rendering a page that is about
   to 401. Anyone can edit the JavaScript that runs on their own machine, so
   nothing here may ever be the only thing standing in front of data.
   ========================================================================== */

export function RequireAuth({ children }: { children: ReactNode }) {
  const { status, retry } = useSession();
  const location = useLocation();

  if (status === 'unavailable') {
    /* NOT a redirect to /login. We do not know whether this session is dead —
       the API could not be reached, or answered 429 — and sending someone to a
       login form says we do know. Their password would fail too, and the
       cookie they still hold would have worked. */
    return (
      <div className="centre" role="alert">
        <p>Cannot reach the server.</p>
        <p className="muted">You are still signed in. Check your connection and try again.</p>
        <button type="button" className="btn" onClick={retry}>
          Try again
        </button>
      </div>
    );
  }

  if (status === 'loading') {
    /* Deliberately blank rather than a spinner. `GET /api/auth/me` answers in
       milliseconds on a local network, and a spinner that flashes for 80ms
       reads as jank. */
    return <div className="centre" aria-busy="true" />;
  }

  if (status === 'anonymous') {
    // Remember where they were headed, so logging in lands them there rather
    // than dumping them on the overview.
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  return <>{children}</>;
}
